import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useDebouncedCallback from 'hooks/useDebouncedCallback'
import useSearchBurnEnabled from 'hooks/useSearchBurnEnabled'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import LoyaltyBurnInfoModal from 'loyalty/components/LoyaltyBurnInfoModal'
import LoyaltyBurnSliderBase from 'loyalty/components/LoyaltyBurnSliderBase'
import { MINIMUM_POINTS_TO_APPLY } from 'loyalty/constants/loyaltyBurn'
import { selectLoggedIn } from 'selectors/accountSelectors'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import getLoyaltyAccountRegion from 'loyalty/selectors/getLoyaltyAccountRegion'
import useLoyaltyBurnExperiment from 'loyalty/hooks/useLoyaltyBurnExperiment'
import { CLEAR_SEARCH_BURN_BATCH_CALCULATIONS } from 'actions/actionConstants'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import getOfferListKey from 'lib/offer/offerListKey'
import useSwitchRegion from 'hooks/useSwitchRegion'
import config from 'constants/config'

interface Props {
  /** Layout passed through to LoyaltyBurnSliderBase. */
  layout?: 'default' | 'filterPanel'
  /** When provided, replaces the URL-offer-list cache-clear key. */
  cacheKey?: string
  /**
   * When true, the slider max is `userPoints` regardless of batch contents.
   * Use when the batch only covers a subset of visible results (e.g. first-page
   * prefetch only) and capping by max(priceInPoints) would cause the max to
   * flicker as more results stream in.
   */
  skipBatchMaxCap?: boolean
  /** When true, skip the Société burn A/B experiment gate. */
  skipBurnExperiment?: boolean
  /** Fires when the toggle is switched on. */
  onBurnEnabled?: () => void
  /** Disables the slider UI without affecting the underlying burn-enabled state. */
  disabled?: boolean
}

function LoyaltyBurnSliderSearch({
  layout,
  cacheKey,
  skipBatchMaxCap = false,
  skipBurnExperiment = false,
  onBurnEnabled,
  disabled = false,
}: Props = {}) {
  const showModal = useContext(ModalContext)
  const [tier] = useCurrentLoyaltyTier()
  const { hasSelectedBurnToggle, setSearchBurnEnabled, pointsToBurn: contextPointsToBurn, setPointsToBurn } = useSearchBurnEnabled()
  const dispatch = useAppDispatch()

  const userPoints = useAppSelector(state => state.loyalty.account.data?.points ?? 0)
  const offerListFilters = useAppSelector(getUrlOfferListFilters)
  const resolvedCacheKey = cacheKey ?? getOfferListKey(offerListFilters)
  const batchCalculations = useAppSelector(state => state.loyalty.pointsBurnBatchCalculations ?? {})
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const isLoyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const isInLoyaltyRegion = useAppSelector(getIsCurrentRegionUserLoyaltyRegion)
  const loyaltyAccountRegion = useAppSelector(getLoyaltyAccountRegion)
  const { isAnyVariant: isBurnEnabled } = useLoyaltyBurnExperiment()

  const loyaltyCurrencyLabel = useMemo(() => {
    return config.regions.find(r => r.code === loyaltyAccountRegion)?.currencyCode
  }, [loyaltyAccountRegion])

  const [switchRegionPath, handleSwitchRegion] = useSwitchRegion(loyaltyAccountRegion)

  useEffect(() => {
    dispatch({ type: CLEAR_SEARCH_BURN_BATCH_CALCULATIONS })
  }, [resolvedCacheKey, dispatch])

  const maxBurnPoints = useMemo(() => {
    if (skipBatchMaxCap) return userPoints
    const maxPriceInPoints = Object.values(batchCalculations)
      .reduce((max, result) => Math.max(max, result?.data?.priceInPoints ?? 0), 0)
    return maxPriceInPoints > 0 ? Math.min(userPoints, maxPriceInPoints) : userPoints
  }, [batchCalculations, userPoints, skipBatchMaxCap])

  const isLoadingMax = !skipBatchMaxCap && hasSelectedBurnToggle && Object.keys(batchCalculations).length === 0

  // Seed from the shared context value (not always the full balance) so a remount -
  // e.g. the mobile filter drawer mounting a fresh instance of this component -
  // doesn't discard whatever amount the user had already selected.
  const [localValue, setLocalValue] = useState<number>(() => contextPointsToBurn ?? userPoints)
  const prevMaxRef = useRef(maxBurnPoints)

  useEffect(() => {
    if (!hasSelectedBurnToggle) {
      prevMaxRef.current = maxBurnPoints
      return
    }
    const prevMax = prevMaxRef.current
    prevMaxRef.current = maxBurnPoints
    setLocalValue(prev => {
      if (prev === prevMax && prev !== maxBurnPoints) {
        setPointsToBurn(maxBurnPoints)
        return maxBurnPoints
      }
      const capped = Math.min(prev, maxBurnPoints)
      if (capped !== prev) setPointsToBurn(capped)
      return capped
    })
  }, [hasSelectedBurnToggle, maxBurnPoints, setPointsToBurn])

  const handleSliderDispatch = useCallback((value: number) => {
    setPointsToBurn(value)
  }, [setPointsToBurn])

  const debouncedSliderDispatch = useDebouncedCallback(handleSliderDispatch, 100, [handleSliderDispatch])

  const onSliderChange = useCallback((_e: React.ChangeEvent<HTMLInputElement>, value: number) => {
    setLocalValue(value)
    debouncedSliderDispatch(value)
  }, [debouncedSliderDispatch])

  const handleToggle = useCallback(async() => {
    if (!isLoggedIn) {
      const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
      if (!result.loggedIn) {
        return
      }
    }

    if (hasSelectedBurnToggle) {
      setLocalValue(maxBurnPoints)
      setSearchBurnEnabled(false)
      setPointsToBurn(undefined)
    } else {
      onBurnEnabled?.()
      setLocalValue(maxBurnPoints)
      setSearchBurnEnabled(true)
      setPointsToBurn(maxBurnPoints)
    }
  }, [isLoggedIn, hasSelectedBurnToggle, maxBurnPoints, setSearchBurnEnabled, setPointsToBurn, showModal, onBurnEnabled])

  const handleReset = useCallback(() => {
    setLocalValue(MINIMUM_POINTS_TO_APPLY)
    setPointsToBurn(MINIMUM_POINTS_TO_APPLY)
  }, [setPointsToBurn])

  const handleInfoClick = useCallback(() => {
    showModal(<LoyaltyBurnInfoModal />)
  }, [showModal])

  const shouldShow = useMemo(() => {
    if (!skipBurnExperiment && !isBurnEnabled) return false
    if (!isLoyaltyEnabled) return false
    if (!isLoggedIn) return true
    if (!hasLoyaltyAccount) return false
    if (!isInLoyaltyRegion) return true
    return userPoints >= MINIMUM_POINTS_TO_APPLY
  }, [skipBurnExperiment, isBurnEnabled, isLoyaltyEnabled, isLoggedIn, hasLoyaltyAccount, isInLoyaltyRegion, userPoints])

  if (!shouldShow) {
    return null
  }

  return (
    <LoyaltyBurnSliderBase
      layout={layout}
      isLoggedIn={isLoggedIn}
      isEnabled={hasSelectedBurnToggle && !disabled}
      loading={isLoadingMax}
      onToggle={handleToggle}
      tier={tier}
      hasLoyaltyAccount={hasLoyaltyAccount}
      userPoints={userPoints}
      maxBurnPoints={maxBurnPoints}
      minBurnPoints={MINIMUM_POINTS_TO_APPLY}
      localValue={localValue}
      onSliderChange={onSliderChange}
      onReset={handleReset}
      onInfoClick={handleInfoClick}
      wrongRegion={isLoggedIn && !isInLoyaltyRegion}
      switchRegionPath={switchRegionPath}
      onSwitchRegion={handleSwitchRegion}
      loyaltyCurrencyLabel={loyaltyCurrencyLabel}
    />
  )
}

export default LoyaltyBurnSliderSearch
